import { useState, useRef, useEffect } from 'react';
import { sendContactMessage } from '../lib/supabase';
import { FaLinkedin, FaGithub, FaEnvelope } from 'react-icons/fa';

export default function Contact() {
  const [form, setForm] = useState({ name: '', email: '', subject: '', message: '' });
  const [status, setStatus] = useState('idle'); // idle | sending | success | error
  const [errorMsg, setErrorMsg] = useState('');
  const [copied, setCopied] = useState(false);
  const refs = useRef([]);

  useEffect(() => {
    const obs = new IntersectionObserver(
      (entries) => entries.forEach(e => { if (e.isIntersecting) e.target.classList.add('visible'); }),
      { threshold: 0.1 }
    );
    refs.current.forEach(r => r && obs.observe(r));
    return () => obs.disconnect();
  }, []);

  const handleChange = (e) => setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      setErrorMsg('Veuillez remplir tous les champs obligatoires.');
      return;
    }
    setStatus('sending');
    setErrorMsg('');

    try {
      // 1. Sauvegarde dans Supabase (optionnel)
      try {
        await sendContactMessage(form);
      } catch (dbErr) {
        console.warn('Sauvegarde BDD échouée', dbErr);
      }

      // 2. Envoi silencieux en arrière-plan via Web3Forms
      const access_key = "647be425-34d3-4ade-b56c-d34e25390c57"; 
      
      if (access_key === "VOTRE_CLE_ICI") {
        alert("ATTENTION: Vous devez insérer votre clé Web3Forms dans le fichier Contact.jsx (ligne 35) pour que l'envoi en arrière-plan fonctionne !");
        setStatus('error');
        setErrorMsg('Clé API manquante.');
        return;
      }

      const response = await fetch("https://api.web3forms.com/submit", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          access_key: access_key,
          name: form.name,
          email: form.email,
          subject: form.subject || "Nouveau contact depuis le Portfolio",
          message: form.message,
          from_name: "Portfolio - " + form.name,
          replyto: form.email
        }),
      });

      const result = await response.json();

      if (result.success) {
        setStatus('success');
        setForm({ name: '', email: '', subject: '', message: '' });
      } else {
        throw new Error(result.message);
      }

    } catch (err) {
      setStatus('error');
      setErrorMsg('Erreur inattendue. Veuillez me contacter directement par email.');
    }
  };

  return (
    <section id="contact">
      <div className="container">
        <div className="contact-grid">
          {/* Left info */}
          <div>
            <p className="section-label reveal" ref={el => refs.current[0] = el}>Contact</p>
            <div className="reveal" ref={el => refs.current[1] = el} style={{ transitionDelay: '0.1s' }}>
              <h2 className="section-title" style={{ marginBottom: 20 }}>
                Construisons quelque<br />chose <em>ensemble</em>
              </h2>
              <p style={{ color: 'var(--white-60)', fontSize: '0.95rem', lineHeight: 1.8, marginBottom: 36 }}>
                Vous avez un projet en tête ? Une question technique ?
                Une opportunité de collaboration ? Je suis toujours ouvert
                à discuter de nouvelles idées.
              </p>
            </div>

            <div className="contact-links reveal" ref={el => refs.current[2] = el} style={{ transitionDelay: '0.2s' }}>
              <a
                className="contact-link-item"
                href="https://www.linkedin.com/in/yassine-alaoui-0a42a9243/"
                target="_blank" rel="noreferrer"
              >
                <div className="contact-link-icon" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}><FaLinkedin size={24} color="#B69A73" /></div>
                <div className="contact-link-text">
                  <div className="contact-link-label">LinkedIn</div>
                  <div className="contact-link-val">linkedin.com/in/yassine-alaoui</div>
                </div>
                <span style={{ color: 'var(--gold)', opacity: 0.5 }}>↗</span>
              </a>

              <a
                className="contact-link-item"
                href="https://github.com/Yassine-ALAOUI-a11"
                target="_blank" rel="noreferrer"
              >
                <div className="contact-link-icon" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}><FaGithub size={24} color="#B69A73" /></div>
                <div className="contact-link-text">
                  <div className="contact-link-label">GitHub</div>
                  <div className="contact-link-val">github.com/Yassine-ALAOUI-a11</div>
                </div>
                <span style={{ color: 'var(--gold)', opacity: 0.5 }}>↗</span>
              </a>

              <div
                className="contact-link-item"
                onClick={() => {
                  navigator.clipboard.writeText('yassinealaoui095@gmail.com');
                  setCopied(true);
                  setTimeout(() => setCopied(false), 2000);
                }}
                style={{ cursor: 'pointer' }}
              >
                <div className="contact-link-icon" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}><FaEnvelope size={24} color="#B69A73" /></div>
                <div className="contact-link-text">
                  <div className="contact-link-label">Email Direct</div>
                  <div className="contact-link-val">yassinealaoui095@gmail.com</div>
                </div>
                <span style={{ color: 'var(--gold)', opacity: 0.8, fontSize: '0.75rem', fontWeight: 600 }}>
                  {copied ? 'Copié !' : '(copier)'}
                </span>
              </div>


            </div>
          </div>

          {/* Right form */}
          <div className="reveal" ref={el => refs.current[3] = el} style={{ transitionDelay: '0.25s' }}>
            <form className="contact-form" onSubmit={handleSubmit}>
              {status === 'success' && (
                <div className="form-success reveal visible">
                  <div className="toast-icon">✓</div>
                  <div className="toast-content">
                    <strong style={{ display: 'block', marginBottom: '4px', color: '#059669' }}>Transmission réussie</strong>
                    Votre message a été transmis avec succès. Je vous ferai un retour dans les plus brefs délais.
                  </div>
                </div>
              )}
              {(status === 'error' || errorMsg) && (
                <div className="form-error reveal visible">
                  <div className="toast-icon">⚠</div>
                  <div className="toast-content">
                    <strong style={{ display: 'block', marginBottom: '4px', color: '#dc2626' }}>Une erreur est survenue</strong>
                    {errorMsg}
                  </div>
                </div>
              )}

              <div className="form-row">
                <div className="field-group">
                  <label className="field-label">Nom *</label>
                  <input
                    type="text"
                    name="name"
                    className="field-input"
                    placeholder="Votre nom"
                    value={form.name}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div className="field-group">
                  <label className="field-label">Email *</label>
                  <input
                    type="email"
                    name="email"
                    className="field-input"
                    placeholder="vous@exemple.com"
                    value={form.email}
                    onChange={handleChange}
                    required
                  />
                </div>
              </div>

              <div className="field-group">
                <label className="field-label">Sujet</label>
                <input
                  type="text"
                  name="subject"
                  className="field-input"
                  placeholder="Collaboration, projet, question..."
                  value={form.subject}
                  onChange={handleChange}
                />
              </div>

              <div className="field-group">
                <label className="field-label">Message *</label>
                <textarea
                  name="message"
                  className="field-textarea"
                  placeholder="Décrivez votre projet ou votre question..."
                  value={form.message}
                  onChange={handleChange}
                  required
                />
              </div>

              <button
                type="submit"
                className="form-submit"
                disabled={status === 'sending'}
              >
                {status === 'sending' ? (
                  <>
                    <span style={{ display: 'inline-block', animation: 'spin 1s linear infinite' }}>↻</span>
                    Envoi en cours...
                  </>
                ) : (
                  <>Envoyer le message →</>
                )}
              </button>

              <p style={{ fontSize: '0.7rem', fontFamily: 'var(--font-mono)', color: 'var(--white-30)', marginTop: 12 }}>
                * Champs obligatoires. Je réponds généralement sous 24-48h.
              </p>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
